import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { selectIsDynamicContentReportingSnippetsFeatureFlagOn } from 'core/userContext';
import { useMetricsContext } from 'modules/productAnalytics/context';
import momentTz from 'moment-timezone';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CUSTOM_RANGE, PAST_7_DAYS, RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { guessCurrentTimeZone } from 'utils/datetime';
import { isEmpty } from '@iterable/utils';
import { getActualTimeZone } from 'utils/timeZone';
import { CampaignFilterDefaults, HighLevelMetricDefaults } from '../constants';
import {
  useAppliedFiltersContext,
  useCampaignAndMetricFiltersContext,
  useTabContext,
  useTabSpecificFiltersContext,
  useTimeFiltersContext,
  useViewContext,
} from '../context';
import {
  selectInsightsFilters,
  selectInsightsFiltersLoaded,
  selectSavedView,
  selectSavedViewLoaded,
} from '../selectors';
import { CampaignFilters, DateParams, Preset, QueryParams, SavedViewParams } from '../types';
import {
  applyQueryTimePresetHelper,
  filterSelectedMetricsByMedium,
  getHighLevelAndMedMetrics,
  getHighLevelAndMedMetricsFromMetrics,
  parseSelectedFilters,
  translateMediumToHighLevelMetrics,
} from '../utils';

export const useFilters = (queryParams: QueryParams) => {
  const { viewId, resetView } = useViewContext();
  const filters = useSelector(selectInsightsFilters) || '';
  const filtersLoaded = useSelector(selectInsightsFiltersLoaded);
  const savedViewLoaded = useSelector(selectSavedViewLoaded);
  const savedView: SavedViewParams = useSelector(selectSavedView);
  const { metricsLoading, analyticsMetrics, highLevelMetrics } = useMetricsContext();
  const { settings } = savedView || {};

  const {
    setSelectedMediums,
    selectedMediums: previouslySelectedMediums,
    setSelectedCrossMetrics,
    setCampaignFilters,
    filteredHLMDefaults,
  } = useCampaignAndMetricFiltersContext();
  const { setDateParams, setTimePreset, setTimezone, timezone } = useTimeFiltersContext();
  const {
    setSelectedSegment,
    setRegion,
    setCampaignsSortDirection,
    setCampaignsSortedBy,
    setAudienceSortDirection,
    setAudienceSortedBy,
    setSnippetsSortDirection,
    setSnippetsSortedBy,
  } = useTabSpecificFiltersContext();
  const { setSelectedTab } = useTabContext();
  const { setAppliedFilters } = useAppliedFiltersContext();
  const defaultTimezone = guessCurrentTimeZone();

  const isDCRFFOn = useSelector(selectIsDynamicContentReportingSnippetsFeatureFlagOn);

  const applyUrlParamFilters = () => {
    const { startTime, endTime, metrics, timezone: tzParam } = queryParams;
    const tz = (tzParam || timezone) as string;
    const otherFilters: any = {};
    const appliedFilterObj: any = {};
    Object.entries(queryParams).forEach(([p, val]) => {
      switch (p) {
        case 'messageMediums': {
          setSelectedMediums(val as string[]);
          appliedFilterObj.mediums = val;
          break;
        }
        case 'endTime':
          break;
        case 'startTime':
          setDateParams({
            startTime: momentTz.tz(val, tz),
            endTime: endTime ? momentTz.tz(endTime, tz) : momentTz().tz(tz),
          });
          setTimePreset(CUSTOM_RANGE);
          break;
        case 'timePreset':
          applyQueryTimePresetHelper(
            val as string,
            startTime as string,
            setDateParams,
            setTimePreset,
            tzParam as string,
            timezone,
          );
          break;
        case 'timezone':
          setTimezone(val as string);
          break;
        case 'metrics': {
          setSelectedCrossMetrics(val as string[]);
          const obj = getHighLevelAndMedMetricsFromMetrics(
            val as string[],
            highLevelMetrics,
            analyticsMetrics,
          );
          appliedFilterObj.appliedCrossMetrics = val;
          appliedFilterObj.mediumMetrics = obj.mediumMetrics;
          appliedFilterObj.overallMetrics = obj.overallMetrics;
          break;
        }
        case 'tab':
          setSelectedTab(val as string);
          break;
        case 'segment':
          setSelectedSegment({ name: val as string });
          break;
        case 'region':
          setRegion([val as string]);
          break;
        case 'campaignsSortBy':
          setCampaignsSortedBy(val as string);
          break;
        case 'campaignsSortDir':
          setCampaignsSortDirection(val as string);
          break;
        case 'audienceSortBy':
          setAudienceSortedBy(val as string);
          break;
        case 'audienceSortDir':
          setAudienceSortDirection(val as string);
          break;
        case 'snippetsSortBy':
          setSnippetsSortedBy(val as string);
          break;
        case 'snippetsSortDir':
          setSnippetsSortDirection(val as SORT_DIRECTIONS);
          break;
        default:
          otherFilters[p] = val;
      }
    });
    setCampaignFilters(otherFilters as CampaignFilters);
    appliedFilterObj.campaignFilters = otherFilters;
    if (!metrics) {
      setSelectedCrossMetrics(filteredHLMDefaults);
      appliedFilterObj.appliedCrossMetrics = filteredHLMDefaults;
      const obj = getHighLevelAndMedMetricsFromMetrics(
        filteredHLMDefaults,
        highLevelMetrics,
        analyticsMetrics,
      );
      appliedFilterObj.mediumMetrics = obj.mediumMetrics;
      appliedFilterObj.overallMetrics = obj.overallMetrics;
    }
    setAppliedFilters(appliedFilterObj);
  };

  const applySavedViewFilters = () => {
    const { selectedMediums, tabOpts, selectedMetrics, timeFilters, campaignFilters } = settings;
    const { startTime, endTime, timezone: filterTZ, timePreset } = timeFilters;
    const {
      selectedSegment,
      region,
      campaignsSortedBy,
      campaignsSortDirection,
      audienceSortedBy,
      audienceSortDirection,
      snippetsSortDirection,
      snippetsSortedBy,
    } = tabOpts;
    const appliedFilterObj: any = {};
    setSelectedMediums(selectedMediums);
    appliedFilterObj.mediums = selectedMediums;

    if (timePreset === CUSTOM_RANGE) {
      setDateParams({
        startTime: momentTz.tz(startTime, filterTZ),
        endTime: endTime ? momentTz.tz(endTime, filterTZ) : momentTz().tz(filterTZ),
      });
    } else {
      // @ts-ignore
      const relativePreset: any = RELATIVE_DATE_PRESETS[timePreset];
      const { startDate, endDate } = relativePreset.format(filterTZ);
      setDateParams({
        startTime: startDate,
        endTime: endDate,
      });
    }
    setTimePreset(timePreset as Preset);
    setTimezone(filterTZ);

    if (selectedMetrics.overallMetrics.length || selectedMetrics.mediumMetrics.length) {
      const overallAndMediumMetrics = [
        ...selectedMetrics.overallMetrics.map((m) => m?.enumName),
        ...selectedMetrics.mediumMetrics.map((m) => m?.name),
      ];

      const metrics = filterSelectedMetricsByMedium(
        selectedMediums,
        overallAndMediumMetrics,
        highLevelMetrics,
        analyticsMetrics,
      );
      setSelectedCrossMetrics(metrics);
      appliedFilterObj.appliedCrossMetrics = overallAndMediumMetrics;
      appliedFilterObj.mediumMetrics = selectedMetrics.mediumMetrics;
      appliedFilterObj.overallMetrics = selectedMetrics.overallMetrics;
    } else {
      setSelectedCrossMetrics(filteredHLMDefaults);
      appliedFilterObj.appliedCrossMetrics = filteredHLMDefaults;
      const obj = getHighLevelAndMedMetrics(
        filteredHLMDefaults,
        highLevelMetrics,
        analyticsMetrics,
      );
      appliedFilterObj.mediumMetrics = obj.mediumMetrics;
      appliedFilterObj.overallMetrics = obj.overallMetrics;
    }
    setSelectedSegment(selectedSegment);
    setRegion(region);
    setCampaignsSortedBy(campaignsSortedBy);
    setCampaignsSortDirection(campaignsSortDirection);
    setAudienceSortedBy(audienceSortedBy);
    setAudienceSortDirection(audienceSortDirection);
    if (isDCRFFOn && snippetsSortedBy) {
      setSnippetsSortedBy(snippetsSortedBy);
    }
    if (isDCRFFOn && snippetsSortDirection) {
      setSnippetsSortDirection(snippetsSortDirection);
    }
    setCampaignFilters(campaignFilters);
    appliedFilterObj.campaignFilters = campaignFilters;
    setAppliedFilters(appliedFilterObj);
  };

  const applyInsightsFilters = () => {
    const {
      selectedMediums,
      selectedChannel,
      selectedMetrics: deprecatedSelectedMetrics,
      selectedCrossMetrics,
      selectedFilterOptions: campaignFilters, // renamed, but still need to update FE-BE handshake
      selectedFilters, // legacy filters
      timePreset,
      dateParams,
      timezone: savedTimezone,
    } = filters;
    const appliedFilterObj: any = {};

    const filterTimezone = savedTimezone ? getActualTimeZone(savedTimezone) : savedTimezone;
    const selectedTimezone = filterTimezone || defaultTimezone;
    // apply medium filters
    const filterMediums = selectedMediums || [selectedChannel];
    setSelectedMediums(filterMediums);
    appliedFilterObj.mediums = filterMediums;

    const selectedMetrics = translateMediumToHighLevelMetrics(
      deprecatedSelectedMetrics,
      selectedCrossMetrics,
    );

    // apply metric filters based on mediums
    const filteredMetrics = filterSelectedMetricsByMedium(
      filterMediums,
      selectedMetrics,
      highLevelMetrics,
      analyticsMetrics,
    );
    setSelectedCrossMetrics(filteredMetrics);

    const obj = getHighLevelAndMedMetrics(filteredMetrics, highLevelMetrics, analyticsMetrics);
    appliedFilterObj.appliedCrossMetrics = filteredMetrics;
    appliedFilterObj.mediumMetrics = obj.mediumMetrics;
    appliedFilterObj.overallMetrics = obj.overallMetrics;
    const parsedCampaignFilters = campaignFilters || parseSelectedFilters(selectedFilters || []);
    appliedFilterObj.campaignFilters = parsedCampaignFilters;

    setCampaignFilters(parsedCampaignFilters);

    setAppliedFilters(appliedFilterObj);

    // apply date + time filters
    // @ts-ignore
    const relativePreset: any = RELATIVE_DATE_PRESETS[timePreset] || {};
    if (relativePreset.format) {
      // @ts-ignore
      const { startDate, endDate } = relativePreset.format(selectedTimezone);
      setDateParams({
        startTime: startDate,
        endTime: endDate,
      });
    } else {
      setDateParams({
        startTime: momentTz.tz((dateParams as DateParams)?.startTime, selectedTimezone),
        endTime: momentTz.tz((dateParams as DateParams)?.endTime, selectedTimezone),
      });
    }
    setTimePreset(timePreset);
    setTimezone(selectedTimezone);
  };

  const applyHardcodedFilters = () => {
    const selectedMediums = previouslySelectedMediums || ['Email'];
    const appliedFilterObj: any = {};
    const timePreset = PAST_7_DAYS as Preset;
    // apply medium filters
    setSelectedMediums(selectedMediums);
    appliedFilterObj.mediums = selectedMediums;
    const selectedCrossMetrics = filterSelectedMetricsByMedium(
      selectedMediums,
      HighLevelMetricDefaults,
      highLevelMetrics,
      analyticsMetrics,
    );
    const selectedFilterOptions = CampaignFilterDefaults;
    // apply metric filters based on mediums
    const filteredMetrics = filterSelectedMetricsByMedium(
      selectedMediums,
      selectedCrossMetrics,
      highLevelMetrics,
      analyticsMetrics,
    );

    setSelectedCrossMetrics(filteredMetrics);

    const obj = getHighLevelAndMedMetrics(filteredMetrics, highLevelMetrics, analyticsMetrics);
    appliedFilterObj.appliedCrossMetrics = filteredMetrics;
    appliedFilterObj.mediumMetrics = obj.mediumMetrics;
    appliedFilterObj.overallMetrics = obj.overallMetrics;
    appliedFilterObj.campaignFilters = selectedFilterOptions;

    setCampaignFilters(selectedFilterOptions);

    setAppliedFilters(appliedFilterObj);

    // apply date + time filters
    // @ts-ignore
    const relativePreset: any = RELATIVE_DATE_PRESETS[timePreset] || {};
    const { startDate, endDate } = relativePreset.format(defaultTimezone);
    setDateParams({
      startTime: startDate,
      endTime: endDate,
    });
    setTimePreset(timePreset);
    setTimezone(defaultTimezone);
  };
  useEffect(() => {
    if (filtersLoaded && (savedViewLoaded || !viewId) && !metricsLoading) {
      if (!viewId && !isEmpty(queryParams)) {
        applyUrlParamFilters();
      } else if (!isEmpty(settings)) {
        applySavedViewFilters();
      } else if (!viewId && !savedView && filters) {
        applyInsightsFilters();
      } else if (!filters && !savedView) {
        applyHardcodedFilters();
      }
    }
  }, [filtersLoaded, metricsLoading, savedViewLoaded, resetView]);
};
